import type { PowerStatus } from '@dia/db';

/**
 * The nine mini-splits, in the order they are drawn. The slug is the Home
 * Assistant object id (climate.<slug>), which is what the island sends as
 * ac_unit.<slug>. Names are the ones on the units' own labels.
 */
export type AcSpot = { slug: string; house: 'hacienda' | 'bungalow'; es: string; en: string };

export const AC_UNITS: readonly AcSpot[] = [
  { slug: 'hacienda_master_bedroom', house: 'hacienda', es: 'Cuarto principal', en: 'Master Bedroom' },
  { slug: 'hacienda_living_room', house: 'hacienda', es: 'Sala', en: 'Living Room' },
  { slug: 'hacienda_bunk_room', house: 'hacienda', es: 'Cuarto de literas', en: 'Bunk Room' },
  { slug: 'hacienda_jungleside_1', house: 'hacienda', es: 'Lado selva 1', en: 'Jungleside 1' },
  { slug: 'hacienda_jungleside_2', house: 'hacienda', es: 'Lado selva 2', en: 'Jungleside 2' },
  { slug: 'hacienda_poolside_1', house: 'hacienda', es: 'Lado piscina 1', en: 'Poolside 1' },
  { slug: 'hacienda_poolside_2', house: 'hacienda', es: 'Lado piscina 2', en: 'Poolside 2' },
  { slug: 'bungalow_bedroom_jungle', house: 'bungalow', es: 'Cuarto de la selva', en: 'Bedroom Jungle' },
  { slug: 'bungalow_water_bedroom', house: 'bungalow', es: 'Cuarto del agua', en: 'Water Bedroom' },
];

export type AcTileMode = 'cooling' | 'sleep' | 'off' | 'unknown';

export type AcTile = AcSpot & { mode: AcTileMode };

export type AcSummary = {
  tiles: AcTile[];
  /** Units that are on (cooling or sleep). */
  on: number;
  /** Units that sent a fresh reading. */
  known: number;
  total: number;
  /** True when every unit that is on is in the sleep preset, and at least one is on. */
  allSleep: boolean;
};

/**
 * Each unit's tile from the status document. A unit that did not report lately
 * is "unknown" and drawn as "no reading": the state of a room is never guessed
 * from its neighbours. The whole thing is null when the island has not sent
 * any unit at all (the older document, or Home Assistant not set up for it).
 */
export function acSummary(ac: PowerStatus['ac'] | undefined): AcSummary | null {
  const units = ac?.units;
  if (!units || units.length === 0) return null;
  const bySlug = new Map(units.map((u) => [u.slug, u.mode]));
  const tiles: AcTile[] = AC_UNITS.map((spot) => ({ ...spot, mode: bySlug.get(spot.slug) ?? 'unknown' }));
  const on = tiles.filter((t) => t.mode === 'cooling' || t.mode === 'sleep').length;
  const known = tiles.filter((t) => t.mode !== 'unknown').length;
  const sleeping = tiles.filter((t) => t.mode === 'sleep').length;
  return { tiles, on, known, total: tiles.length, allSleep: on > 0 && sleeping === on };
}
